import { useTranslation } from "react-i18next";
import { Col, Form, Input, InputNumber, Modal, Row } from "antd";
import { doc, updateDoc } from "firebase/firestore";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import TextArea from "antd/es/input/TextArea";
import { DELIVERY_DAYS } from "../../../../../constants/OrderConstant";
import { CURRENCY, DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateExactAccount } from "../../../../../functions/api/exact";
import { showOperationSummary } from "../../../../../utils/operationSummary";
import { formatPhoneNumber } from "../../../../../functions/formatters";
import { InfoCircleOutlined } from "@ant-design/icons";

export default function QuickEditModal({ company, visible, onClose, exactCustomers }) {
  const { t } = useTranslation();
  const [quickEditForm] = Form.useForm();
  const { email } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (company) {
      quickEditForm.setFieldsValue({
        customerDeliveryDays: company.customerDeliveryDays,
        customerEmail: company.customerEmail,
        customerPhone: company.customerPhone,
        customerSnelstartDiscount: company.customerSnelstartDiscount || 0,
        deliveryCost: company.deliveryCost || DEFAULT_DELIVERY_COST,
        deliveryCostThreshold: company.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
        deliveryNotes: company.deliveryNotes,
        deliveryLabel: company.deliveryLabel,
        internalNotes: !!company.internalNotes ? company.internalNotes.text : null,
      });
    }
  }, [company]);

  const handleSaveQuickChanges = () => {
    quickEditForm.validateFields().then(async (values) => {
      setIsLoading(true);

      // Initialize operation results
      const operationResults = {
        firestore: { attempted: true, success: false, error: null },
        exact: { attempted: false, success: false, error: null },
      };

      const obj = {
        customerDeliveryDays: values.customerDeliveryDays || null,
        customerEmail: values.customerEmail,
        customerPhone: values.customerPhone ? formatPhoneNumber(values.customerPhone) : null,
        customerSnelstartDiscount: values.customerSnelstartDiscount || 0,
        deliveryCost: values.deliveryCost || DEFAULT_DELIVERY_COST,
        deliveryCostThreshold: values.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
        deliveryNotes: values.deliveryNotes || null,
        deliveryLabel: values.deliveryLabel || null,
        internalNotes: null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      if (values.internalNotes) {
        if (!!company.internalNotes && company.internalNotes.text !== values.internalNotes)
          obj.internalNotes = {
            ...company.internalNotes,
            text: values.internalNotes,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          };
        else {
          obj.internalNotes = { text: values.internalNotes, createdAt: dayjs().unix(), createdBy: email };
        }
      }

      // Update Firestore first
      try {
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), obj);

        operationResults.firestore.success = true;

        // If customer has exactId, also update Exact
        if (company.exactId) {
          operationResults.exact.attempted = true;
          try {
            const token = await getFirebaseIDToken();
            const exactUpdateObj = {
              exactAccountId: company.exactId,
              customerName: company.customerName,
              invoiceEmail: values.customerEmail,
              customerPhone: values.customerPhone,
              customerDeliveryDays: values.customerDeliveryDays,
              customerDiscount: values.customerSnelstartDiscount,
            };

            await updateExactAccount(exactUpdateObj, token);
            operationResults.exact.success = true;
          } catch (exactError) {
            console.error("Exact update error:", exactError);
            // Extract the most detailed error message available
            const detailedError =
              exactError.response?.data?.Message || exactError.response?.data?.message || exactError.message;
            operationResults.exact.error = detailedError;
          }
        }

        setIsLoading(false);
        onClose();
        showOperationSummary(operationResults, t);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        operationResults.firestore.error = firestoreError.message;
        setIsLoading(false);
        showOperationSummary(operationResults, t);
      }
    });
  };

  return (
    <Modal
      title={t("quick_edit_details", { company: !!company ? company.customerName : "" })}
      open={visible}
      onOk={handleSaveQuickChanges}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      zIndex={1002}
      centered
    >
      <Form layout="vertical" form={quickEditForm}>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerEmail"}
              label={t("email")}
              rules={[{ required: true, message: t("form.enter_email") }]}
            >
              <Input placeholder={t("email")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerPhone"}
              label={t("phone")}
              rules={[
                {
                  pattern: /^[+]?(?:[0-9][\s\-]*){10,15}$/,
                  message: t("form.enter_valid_phone"),
                },
                {
                  type: "tel",
                },
              ]}
              tooltip={{ title: t("enter_phone_tooltip"), icon: <InfoCircleOutlined /> }}
            >
              <Input placeholder={t("phone")} />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name={"deliveryCost"} label={t("delivery_cost")} initialValue={DEFAULT_DELIVERY_COST}>
              <InputNumber
                placeholder={t("delivery_cost")}
                style={{ width: "100%" }}
                min={0}
                precision={2}
                step={0.05}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item
              name={"deliveryCostThreshold"}
              label={t("delivery_cost_threshold")}
              initialValue={FREE_DELIVERY_FROM_PRICE}
            >
              <InputNumber
                placeholder={t("delivery_cost_threshold")}
                style={{ width: "100%" }}
                precision={2}
                step={0.5}
                min={0}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name={"customerDeliveryDays"} label={t("delivery_days")} initialValue={DELIVERY_DAYS}>
              <Input placeholder={t("delivery_days")} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name={"customerSnelstartDiscount"} label={t("customer_discount")} initialValue={0}>
              <InputNumber
                placeholder={t("customer_discount")}
                style={{ width: "100%" }}
                min={0}
                max={100}
                formatter={(value) => `${value}%`}
                parser={(value) => (value === null || value === void 0 ? void 0 : value.replace("%", ""))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name={"deliveryNotes"} label={t("delivery_notes")}>
              <TextArea placeholder={t("delivery_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Form.Item name={"internalNotes"} label={t("internal_notes")}>
              <TextArea placeholder={t("internal_customer_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
